import { useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import { toast } from 'material-react-toastify';
import { isEmpty } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { getEmptyPaymentForm } from '../../interfaces/Checkout/PaymentForm';
import { useUserSummary } from '../../hooks/useUserSummary';
import { bodyWidth } from '../../utils/Style';
import { CheckoutStepper } from './CheckoutStepper';
import {
  CheckoutStep,
  checkoutSteps,
  validateAddress,
  validatePayment,
  validateProfile,
} from './utils';
import { useIsMobile } from '../../hooks/useIsMobile';
import { CheckoutProfile } from './CheckoutProfile';
import { ShippingMethod } from '../../interfaces/Shipping/ShippingMethod';
import { getShippingMethods } from '../../api/Shipping';
import { useBasket } from '../../hooks/useBasket';
import { BreakdownCard } from './BreakdownCard';
import { useThemeColor } from '../../hooks/useThemeColor';
import { CouponCard } from './CouponCard';
import { PointCard } from './PointCard';
import { isTokenValid } from '../../utils/Auth';
import { CheckoutAddressSelect } from './CheckoutAddressSelect';
import { CheckoutGuestAddress } from './CheckoutGuestAddress';
import { BankTransferInstructions } from '../../interfaces/Checkout/BankTransferInstructions';
import { CheckoutPayment } from './CheckoutPayment';
import { checkout } from '../../api/Checkout';
import { clearStoredBasket, getStoredBasket } from '../../utils/Basket';
import { Waiting } from '../../components/Waiting/Waiting';
import { GenericHelmet } from '../../components/Helmet/GenericHelmet';

export const CheckoutPage = () => {
  const navigate = useNavigate();

  const { userSummary } = useUserSummary();
  const { totalAmount: subtotal, basket } = useBasket();

  const isMobile = useIsMobile();
  const { primary, secondary } = useThemeColor();

  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
  const [bankTransferInstructions, setBankTransferInstructions] = useState<
    BankTransferInstructions | undefined
  >(undefined);

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(getEmptyPaymentForm());
  const [currentStep, setCurrentStep] = useState<CheckoutStep>('profile');

  const isLastStep = isMobile || currentStep === 'payment';

  const profileErrors = validateProfile(form);
  const addressErrors = validateAddress(form);
  const paymentErrors = validatePayment(form);

  const isAllValid =
    isEmpty(profileErrors) && isEmpty(addressErrors) && isEmpty(paymentErrors);

  useEffect(() => {
    getShippingMethods()
      .then((res) => {
        setShippingMethods(res.shippingMethods);
        setBankTransferInstructions(res.bankTransferInstructions);
      })
      .catch((err) => toast.error(err.message));
  }, []);

  useEffect(() => {
    let updatedForm = { ...form };

    if (userSummary) {
      updatedForm = {
        ...updatedForm,
        firstName: userSummary.firstName,
        lastName: userSummary.lastName,
        email: userSummary.email,
        phone: userSummary.phone,
      };
    }
    if (shippingMethods.length) {
      updatedForm.shippingMethod = shippingMethods[0].id;
    }

    setForm(updatedForm);
  }, [userSummary, shippingMethods]);

  const handleSubmit = () => {
    if (isLastStep) {
      setLoading(true);
      checkout({
        ...form,
        dynamicBasket: isTokenValid() ? [] : getStoredBasket(),
      })
        .then((url) => {
          toast.success(
            url
              ? '3d linkine yönlendiriliyorsunuz...'
              : 'Sipariş oluşturuldu. Lütfen E-Mail ile iletilen yönergeleri takip edin.',
          );

          if (!isTokenValid()) {
            clearStoredBasket();
          }

          if (url) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(url, 'text/html');
            const parsedForm = doc.querySelector('form');

            if (parsedForm) {
              document.body.appendChild(parsedForm); // Add the form to the DOM
              parsedForm.submit(); // Submit the form
            } else {
              toast.error('Payment form not found in response.');
            }
          } else {
            navigate('/payment/info');
          }
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
      return;
    }

    setCurrentStep(checkoutSteps[checkoutSteps.indexOf(currentStep) + 1]);
  };

  const total = Math.max(
    0,
    subtotal - (form.point ?? 0) - (form.couponAmount ?? 0),
  );
  const isFreeShipping =
    !!basket?.freeShippingAmount && total >= basket.freeShippingAmount;

  const shippingMethod = shippingMethods.find(
    (x) => x.id === form.shippingMethod,
  );

  const grandTotal =
    total +
    ((isFreeShipping
      ? shippingMethod?.discountedPrice
      : shippingMethod?.price) ?? 0);

  const breakdownSection = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: isMobile ? '100%' : 300,
        flexShrink: 0,
        mt: isMobile ? 0 : '40px',
      }}
    >
      <GenericHelmet title='Siparişi Tamamla' />

      <CouponCard
        form={form}
        onUseCoupon={(coupon) =>
          setForm({ ...form, couponID: coupon.id, couponAmount: coupon.amount })
        }
      />

      {!!userSummary && !!basket && (
        <PointCard
          form={form}
          subtotal={subtotal}
          basketSummary={basket}
          userSummary={userSummary}
          onUsePoint={(point) => setForm({ ...form, point })}
        />
      )}

      <BreakdownCard
        form={form}
        shippingMethods={shippingMethods}
        subtotal={subtotal}
        grandTotal={grandTotal}
        isFreeShipping={isFreeShipping}
        onRemovePoint={() => setForm({ ...form, point: undefined })}
        onRemoveCoupon={() =>
          setForm({ ...form, couponID: undefined, couponAmount: undefined })
        }
      />

      <Button
        variant='contained'
        disabled={isLastStep && !isAllValid}
        onClick={handleSubmit}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '14px',
          color: secondary,
          backgroundColor: primary,
        }}
      >
        {isLastStep ? 'Siparişi Onayla' : 'Devam Et'}
      </Button>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
      }}
    >
      <Waiting open={loading} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: bodyWidth,
          py: 3,
          px: 1.5,
          gap: 2.5,
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {!isMobile && (
            <CheckoutStepper
              form={form}
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
            />
          )}

          {(isMobile || currentStep === 'profile') && (
            <CheckoutProfile form={form} onChange={setForm} />
          )}

          {(isMobile || currentStep === 'address') &&
            (isTokenValid() ? (
              <CheckoutAddressSelect
                shippingAddressID={form.addressID}
                billingAddressID={form.billingAddressID}
                onChange={(newShippingAddressID, newBillingAddressID) =>
                  setForm({
                    ...form,
                    addressID: newShippingAddressID,
                    billingAddressID: newBillingAddressID,
                  })
                }
              />
            ) : (
              <CheckoutGuestAddress
                form={form}
                onChange={(guestAddress) => setForm({ ...form, guestAddress })}
              />
            ))}

          {(isMobile || currentStep === 'payment') && (
            <CheckoutPayment
              form={form}
              onChange={setForm}
              shippingMethods={shippingMethods}
              bankTransferInstructions={bankTransferInstructions}
              totalAmount={grandTotal}
              isFreeShipping={isFreeShipping}
            />
          )}

          {isMobile && breakdownSection}
        </Box>

        {!isMobile && breakdownSection}
      </Box>
    </Box>
  );
};
